import { useEffect, useMemo, useRef } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Timeframe, VACANCY_START_AVAILABILITY } from "../../../models/vacancy";
import CustomButton from "../Buttons/CustomButton";
import RoundedPlus from "../Icons/RoundedPlus";
import TimeframeBlock from "./TimeframeBlock";
import { updateTimeframesBasedOnAvailability } from "./timeframeHelpers";

interface ITimeframeSelectorProps {
  isEdit: boolean;
  startAvailability: VACANCY_START_AVAILABILITY;
  value: Timeframe[]; // Formik values.timeframes
  onChange: (value: Timeframe[]) => void; // Formik setFieldValue("timeframes", value)
  error?: string | string[]; // error from Formik validation
}

// Extracts the first useful error message from nested error structure
export const getTimeframeErrorMessage = (error: any): string => {
  if (!error) return "";

  // If Yup test-level error string (from .test)
  if (typeof error === "string") return error;

  // If it's an array of nested field errors
  if (Array.isArray(error)) {
    for (const item of error) {
      if (typeof item === "string") return item;
      if (typeof item === "object") {
        const messages = Object.values(item).filter(Boolean);
        if (messages.length) return messages[0] as string;
      }
    }
  }
  if (typeof error === "object") {
    const messages = Object.values(error).filter(Boolean);
    if (messages.length) return messages[0] as string;
  }

  return "";
};

export const isTimeframeDisabled = (tf: Timeframe): boolean => {
  if (!tf?.date?.[1]) return false;

  const endDate = new Date(tf.date[1]);
  const currentTime = new Date();
  const endTimeParts = tf.end_time.split(":");

  endDate.setHours(Number(endTimeParts[0]), Number(endTimeParts[1]), 0);

  return endDate < currentTime;
};

// Main Component
const TimeframeSelector = ({
  isEdit,
  startAvailability,
  value,
  error,
  onChange,
}: ITimeframeSelectorProps) => {
  const timeframes = value;

  // Predefined dropdown options for time (00:00 - 23:45 in 15-minute intervals)
  const timeOptionObjects = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
      const period = hour < 12 ? "am" : "pm";
      const key = hour.toString().padStart(2, "0");

      options.push({ value: `${key}:00`, label: `${formattedHour}:00${period}` });
      options.push({ value: `${key}:15`, label: `${formattedHour}:15${period}` });
      options.push({ value: `${key}:30`, label: `${formattedHour}:30${period}` });
      options.push({ value: `${key}:45`, label: `${formattedHour}:45${period}` });
    }
    options.push({ value: "23:59", label: "12:00am" });
    return options;
  }, []);

  // Get the current date and time
  const getCurrentDate = () => {
    return new Date();
  };

  const didMountRef = useRef(false);

  // Updates timeframes based on availability rules.
  useEffect(() => {
    if (!startAvailability || isEdit) return;

    const localDate = getCurrentDate();
    const updated = updateTimeframesBasedOnAvailability(
      timeframes,
      startAvailability,
      timeOptionObjects,
      localDate
    );

    if (JSON.stringify(updated) !== JSON.stringify(timeframes)) {
      onChange(updated);
    }
  }, [startAvailability, timeOptionObjects]);

  // Updates a specific field (date/start_time/end_time) of a timeframe by ID
  type TimeframeUpdate = Partial<Omit<Timeframe, "id">>; // all fields except id

  const handleChangeMultipleFields = (id: number, updates: TimeframeUpdate) => {
    const updated = timeframes.map((tf) =>
      tf.id === id ? { ...tf, ...updates } : tf
    );
    onChange(updated);
  };

  // Adds a new empty timeframe
  const addTimeframe = () => {
    const updated = [
      ...timeframes,
      { id: Date.now(), date: null, start_time: "", end_time: "" },
    ];
    onChange(updated);
  };

  // Removes a timeframe by ID
  const removeTimeframe = (id: number) => {
    const updated = timeframes.filter((tf) => tf.id !== id);

    const first = updated[0];

    const isFirstEmpty =
      !first ||
      !first.date ||
      !first.date[0] ||
      !first.date[1] ||
      !first.start_time ||
      !first.end_time;

    // If first is empty and we are in create mode and availability exists
    if (!isEdit && startAvailability && isFirstEmpty) {
      const localDate = getCurrentDate();
      const withDefaultFirst = updateTimeframesBasedOnAvailability(
        updated.length
          ? updated
          : [{ id: Date.now(), date: null, start_time: "", end_time: "" }],
        startAvailability,
        timeOptionObjects,
        localDate
      );
      onChange(withDefaultFirst);
    } else {
      onChange(updated);
    }
  };

  // Ensures the last timeframe is completely filled before allowing a new one
  const isLastTimeframeValid = () => {
    const last = timeframes[timeframes.length - 1];
    return (
      last?.date?.[0] && last?.date?.[1] && last.start_time && last.end_time
    );
  };

  return (
    <>
      {/* Render all TimeframeBlocks */}
      {timeframes.map((tf, idx) => (
        <TimeframeBlock
          key={tf.id}
          tf={tf}
          index={idx}
          startAvailability={startAvailability}
          timeOptions={timeOptionObjects}
          onChange={handleChangeMultipleFields}
          onRemove={timeframes.length > 1 ? removeTimeframe : undefined}
          error={error?.[idx] || ""}
          disabled={isEdit && isTimeframeDisabled(tf)}
        />
      ))}

      {/* Button to add new timeframe */}
      <div className="d-flex justify-content-center">
        <CustomButton
          variant="primary"
          type="button"
          className="btn-theme bg-white text-black mobile-w-100"
          onClick={addTimeframe}
          disabled={!isLastTimeframeValid()} // Disable if previous is incomplete
        >
          <RoundedPlus fill="black" />
          Add Another Timeframe
        </CustomButton>
      </div>
    </>
  );
};

export default TimeframeSelector;
